import React, { useMemo, FC, useCallback } from "react";
import DownLtLogoBlue from "@svgIcons/DownIt";
import styles from "./programs.module.scss";
import Link from "next/link";
import ArrowRight from "@/SvgIcons/ArrowRight";
import AI from "@/SvgIcons/AI";
import SponsoredLogo from "@/components/atoms/SponsoredLogo";
import { GAEvent } from "@/helpers/gaEvents";
import { getGlobalStateValue } from "@/context/utils";
import { pushToDataLayer } from "@/helpers/gtag";

export const USRemover = (title: string): string => title.split("_").join(" ");
interface ProgramsProps {
  setMenuPage: (page: number) => void;
  setProgramsDetails: (details: {
    categoryTitle: string;
    programsList: any[];
    categoryParentTitle: string;
  }) => void;
  showPrograms?: boolean;
  closeMenuHandler?: (href:string) => void;
  headerMenuList?: any;
}
interface ProgramCategory {
  [key: string]: any;
}
interface ProgramItemProps {
  programCategory: ProgramCategory;
  programTitle: string;
  headerMenuList: any;
  onTitleClickHandler: (categoryTitle: string, programCategory: any, programTitle:string) => void;
}

const ProgramItem: FC<ProgramItemProps> = React.memo(
  ({ programCategory, programTitle, onTitleClickHandler,headerMenuList }) => {
    const { NAVIGATION_CLICK } = GAEvent;

    const showCollaboration = useMemo(
      () =>
        [
          "ADD-ON INDUSTRY CERTIFICATIONS",
          "INDUSTRY-ALIGNED PROGRAMS",
        ].includes(programTitle),
      [programTitle]
    );

    if (programTitle === "PROGRAM WISE") {
      programCategory = {
        "All Programs": headerMenuList["MAIN_LIST"],
        ...programCategory,
      };
    }

    const fireGTEvents = ({ header_title }) => {
      setTimeout(() => {
        pushToDataLayer({
          event: NAVIGATION_CLICK,
          cta_text: header_title || "",
          category: "Programs",
          sub_category: programTitle || "NA",
          section_name: "hamburger",
          redirection_url: "NA",
          page_type: getGlobalStateValue("pageType") || "Home Page",
          header_title: header_title || "NA",
        });

        window?.webengage?.track("Program Menu Selected", {
          "Category Name": programTitle,
          "Sub-Category Name": header_title,
        });
      }, 1500);
    };

    const handleFireGTAEvent = (linkType: string) => {
      setTimeout(() => {
        if (linkType === "STUDY_ABROAD") {
          pushToDataLayer({
            event: NAVIGATION_CLICK,
            cta_text: "Study Abroad",
            category: "Programs",
            sub_category: "PROGRAM WISE",
            section_name: "hamburger",
            page_type: getGlobalStateValue("pageType") || "Home Page",
            header_title: "Study Abroad"
          });

          window?.webengage?.track("Program Menu Selected", {
            "Category Name": "PROGRAM WISE",
            "Sub Category Name": "Study Abroad",
            "event_name": "Program Menu Selected"
          })
        } else if (linkType === "SCHOOL_OF_AI") {
          pushToDataLayer({
            event: NAVIGATION_CLICK,
            cta_text: "School Of AI",
            category: "Programs",
            sub_category: "PROGRAM WISE",
            section_name: "hamburger",
            page_type: getGlobalStateValue("pageType") || "Home Page",
            header_title: "School Of AI"
          });

          window?.webengage?.track("Program Menu Selected", {
            "Category Name": "PROGRAM WISE",
            "Sub Category Name": "School Of AI",
            "event_name": "Program Menu Selected",
          })
        }
      }, 1500);
    }

    return (
      <div className={styles.programWrapper}>
        <div className={styles.programHeadTitle}>
          {programTitle}
          {showCollaboration && (
            <>
              <br />
              In collaboration with
            </>
          )}
        </div>
        {Object.keys(programCategory).map((category, index) => {
          const categoryTitle = USRemover(category);
          const tagName = programCategory?.[category]?.signifier?.tagName || "";

          return (
            <div
              key={index}
              className={styles.category}
              onClick={() => {
                onTitleClickHandler(categoryTitle, programCategory[category], programTitle);
                fireGTEvents({ header_title: categoryTitle });
              }}
            >
              <span
                className={`${styles.categoryTitle}`}
              >
                {categoryTitle === "Artificial Intelligence" && <AI />}
                {categoryTitle === "Machine Learning" && <SponsoredLogo type="BRAIN_STROM" />}
                {categoryTitle === "KPMG PROGRAM" ? "KPMG" : categoryTitle}
                {tagName && (
                  <span className={`${styles[tagName]} ${styles.tag}`}>
                    {tagName}
                  </span>
                )}
              </span>
              <DownLtLogoBlue
                className={styles.rightArrow}
                width={13}
                height={9}
              />
            </div>
          );
        })}
        {programTitle === "PROGRAM WISE" && <Link
          className={`${styles.category}`}
          href={`${process.env.NEXT_PUBLIC_SCHOOL_OF_API}?utm_source=amityonline&utm_medium=megamenu`}
          onClick={() => handleFireGTAEvent("SCHOOL_OF_AI")}
        >
          <span className={`${styles.categoryTitle}`}>
            School of AI
            <span className={`${styles["New"]} ${styles.tag}`}>
              New
            </span>
          </span>
          <DownLtLogoBlue
            className={styles.rightArrow}
            width={13}
            height={9}
          />
        </Link>}
        {programTitle === "PROGRAM WISE" && <Link prefetch={false}
          className={`${styles.category}`}
          href="https://studyabroad.amityonline.com?utm_source=amityonline&utm_medium=megamenu"
          target="_self"
          onClick={() => handleFireGTAEvent("STUDY_ABROAD")}
        >
          <span className={`${styles.categoryTitle}`}>
            Study Abroad
            <span className={`${styles["New"]} ${styles.tag}`}>
              New
            </span>
          </span>
          <DownLtLogoBlue
            className={styles.rightArrow}
            width={13}
            height={9}
          />
        </Link>}
      </div>
    );
  }
);

const Programs: FC<ProgramsProps> = ({
  setMenuPage,
  setProgramsDetails,
  showPrograms,
  closeMenuHandler,
  headerMenuList
}) => {
  const KEY_TO_LABEL_MAP: Record<string, string> = {
    PROGRAM_WISE: "PROGRAM WISE",
    ADD_ON_INDUSTRY_CERTIFICATIONS: "ADD-ON INDUSTRY CERTIFICATIONS",
    INDUSTRY_ALIGNED_PROGRAM: "INDUSTRY-ALIGNED PROGRAMS",
    Trending_programs: "TRENDING PROGRAMS",
    DOMAIN_WISE: "DOMAIN WISE",
  };

  const PROGRAMS_CATEGORY_KEY = useMemo(
    () => Object.keys(headerMenuList).filter((key) => key !== "MAIN_LIST"),
    []
  );

  const onTitleClickHandler = useCallback(
    (categoryTitle: string, programCategory: any, programTitle: string): void => {
      setProgramsDetails({
        categoryParentTitle: programTitle,
        categoryTitle,
        programsList: Array.isArray(programCategory)
          ? programCategory
          : programCategory.items,
      });
      setMenuPage(2);
    },
    [setMenuPage, setProgramsDetails]
  );

  const goBack = useCallback((): void => {
    setMenuPage(0);
  }, [setMenuPage]);

  const fireGTEvent = (href) => {
    setTimeout(() => {
      pushToDataLayer({
        event: GAEvent.NAVIGATION_CLICK,
        cta_text: "Explore All Programs",
        category: "Programs",
        sub_category: "Domain wise",
        section_name: "header",
        redirection_url: "/programs",
        page_type: getGlobalStateValue("pageType") || "Home Page",
        header_title: "NA",
      });
    }, 2000);
    closeMenuHandler(href);
  };

  return (
    <div
      className={`${styles.programsWrapper} ${showPrograms ? styles.open : ""}`}
    >
      <div className={styles.arrowContainer} onClick={goBack} >
        <DownLtLogoBlue className={styles.leftArrow} width={15} height={15} />
      </div>
      <div>
        {PROGRAMS_CATEGORY_KEY.map((label, index) => {
          const programCategory = headerMenuList[label];
          const programTitle = KEY_TO_LABEL_MAP[label] || USRemover(label);
          return (
            <ProgramItem
              key={index}
              programTitle={programTitle}
              programCategory={programCategory}
              onTitleClickHandler={onTitleClickHandler}
              headerMenuList={headerMenuList}
            />
          );
        })}
      </div>
      <Link prefetch={false}
        href="/programs"
        className={styles.exploreCTA}
        onClick={() => fireGTEvent("/programs")}
      >
        Explore All Programs
        <ArrowRight color="#1a325d" width={19} height={19} />
      </Link>
    </div>
  );
};

export default Programs;
